import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Container, PageTitle, GameContainer, Button, FlexContainer } from '../../styles/StyledComponents';
import { useGameContext } from '../../context/GameContext';

const VaultContainer = styled.div<{ isCompleted?: boolean }>`
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  margin: 2rem auto;
  position: relative;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  border: 4px solid #B8860B;
  transition: all 0.5s ease;
  transform: ${props => props.isCompleted ? 'scale(1.1) rotate(5deg)' : 'scale(1)'};
  animation: ${props => props.isCompleted ? 'vault-celebration 0.8s ease-in-out' : 'none'};

  &::before {
    content: '';
    position: absolute;
    width: 40px;
    height: 60px;
    background-color: #8B4513;
    border-radius: 8px;
    top: 50px;
    left: 50%;
    transform: translateX(-50%);
  }

  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    background-color: #2C1810;
    border-radius: 50%;
    top: 70px;
    left: 50%;
    transform: translateX(-50%);
  }

  @keyframes vault-celebration {
    0% { transform: scale(1) rotate(0deg); }
    25% { transform: scale(1.2) rotate(-5deg); }
    50% { transform: scale(1.3) rotate(5deg); }
    75% { transform: scale(1.2) rotate(-3deg); }
    100% { transform: scale(1.1) rotate(5deg); }
  }
`;

const WordDisplay = styled.div`
  font-size: 2.5rem;
  font-family: var(--font-family) !important;
  text-align: center;
  margin: 2rem 0;
  padding: 1.5rem;
  background: linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%);
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  border: 3px solid #2196F3;

  @media (max-width: 768px) {
    font-size: 2rem;
    padding: 1rem;
  }
`;

const LetterSlots = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin: 2rem 0;
  gap: 0.5rem;
`;

const LetterSlot = styled.div<{ isRevealed: boolean; isCorrect?: boolean }>`
  width: 60px;
  height: 60px;
  border: 3px solid ${props => props.isCorrect === false ? 'var(--error-color)' : 'var(--secondary-color)'};
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  background-color: ${props => {
    if (props.isCorrect === false) return 'rgba(244, 67, 54, 0.1)';
    if (props.isRevealed) return 'rgba(76, 175, 80, 0.2)';
    return 'white';
  }};
  color: ${props => props.isRevealed ? 'var(--success-color)' : 'transparent'};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }
`;

const LetterButtons = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin: 2rem 0;
  gap: 0.5rem;
`;

const LetterButton = styled.button<{ isUsed?: boolean }>`
  width: 50px;
  height: 50px;
  font-size: 1.5rem;
  font-weight: bold;
  border-radius: var(--border-radius);
  background-color: ${props => props.isUsed ? 'rgba(0, 0, 0, 0.1)' : 'var(--primary-color)'};
  color: ${props => props.isUsed ? 'rgba(0, 0, 0, 0.3)' : 'white'};
  border: 2px solid ${props => props.isUsed ? 'rgba(0, 0, 0, 0.1)' : 'var(--primary-color)'};
  cursor: ${props => props.isUsed ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  text-transform: uppercase;

  &:hover:not(:disabled) {
    background-color: var(--secondary-color);
    transform: translateY(-2px);
  }

  &:disabled {
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    font-size: 1.25rem;
  }
`;

const FeedbackMessage = styled.div<{ isCorrect: boolean }>`
  font-size: 1.5rem;
  font-family: var(--font-family) !important;
  text-align: center;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: var(--border-radius);
  background-color: ${props => props.isCorrect ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'};
  color: ${props => props.isCorrect ? 'var(--success-color)' : 'var(--error-color)'};

  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 0.75rem;
  }
`;

const ScoreDisplay = styled.div`
  font-size: 1.2rem;
  font-family: var(--font-family) !important;
  text-align: right;
  margin-bottom: 1rem;

  span {
    font-weight: bold;
    color: var(--primary-color);
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 10px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  margin: 1rem 0;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ percentage: number }>`
  width: ${props => props.percentage}%;
  height: 100%;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  transition: width 0.3s ease;
`;

const DifficultyFilters = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem 0;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ isActive: boolean }>`
  padding: 0.5rem 1rem;
  font-size: 1rem;
  font-weight: bold;
  border-radius: var(--border-radius);
  background-color: ${props => props.isActive ? 'var(--primary-color)' : 'white'};
  color: ${props => props.isActive ? 'white' : 'var(--primary-color)'};
  border: 2px solid var(--primary-color);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.isActive ? 'var(--secondary-color)' : 'rgba(33, 150, 243, 0.1)'};
  }

  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 2rem 0;
  animation: fade-in 0.5s ease-in-out;

  @keyframes fade-in {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const LoadingText = styled.div`
  font-size: 1.5rem;
  font-family: var(--font-family) !important;
  text-align: center;
  margin: 1rem 0;
  color: var(--primary-color);
  animation: pulse 1s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
`;

const SparkleContainer = styled.div`
  position: relative;
  display: inline-block;

  &::before, &::after {
    content: '✨';
    position: absolute;
    font-size: 2rem;
    animation: sparkle 1.5s ease-in-out infinite;
  }

  &::before {
    top: -10px;
    left: -30px;
    animation-delay: 0s;
  }

  &::after {
    bottom: -10px;
    right: -30px;
    animation-delay: 0.5s;
  }

  @keyframes sparkle {
    0%, 100% { opacity: 0; transform: scale(0.5) rotate(0deg); }
    50% { opacity: 1; transform: scale(1) rotate(180deg); }
  }
`;

interface Word {
  word: string;
  hint: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

interface GameState {
  currentWord: Word;
  revealedLetters: boolean[];
  usedLetters: string[];
  availableLetters: string[];
  incorrectGuesses: number;
  showHint: boolean;
  gameStatus: 'playing' | 'completed' | 'failed';
  difficultyFilter: 'all' | 'easy' | 'medium' | 'hard';
  isLoading: boolean;
}

const VOCABULARY_WORDS: Word[] = [
  // Easy difficulty (3-6 letters) - 70% of total (70 words)
  // 3-letter words
  { word: 'CAT', hint: '🐱 Furry pet that says meow', category: 'Animals', difficulty: 'easy' },
  { word: 'DOG', hint: '🐶 Pet that barks and wags its tail', category: 'Animals', difficulty: 'easy' },
  { word: 'SUN', hint: '☀️ Bright yellow ball in the sky', category: 'Nature', difficulty: 'easy' },
  { word: 'BAT', hint: '🦇 Flying animal that sleeps upside down', category: 'Animals', difficulty: 'easy' },
  { word: 'CAR', hint: '🚗 Vehicle with four wheels', category: 'Transportation', difficulty: 'easy' },
  { word: 'BED', hint: '🛏️ Where you sleep at night', category: 'Home', difficulty: 'easy' },
  { word: 'BOX', hint: '📦 Container to put things in', category: 'Objects', difficulty: 'easy' },
  { word: 'HAT', hint: '👒 What you wear on your head', category: 'Clothing', difficulty: 'easy' },
  { word: 'CUP', hint: '☕ Container for drinking', category: 'Objects', difficulty: 'easy' },
  { word: 'EGG', hint: '🥚 White oval thing from chickens', category: 'Food', difficulty: 'easy' },
  
  // 4-letter words
  { word: 'BALL', hint: '⚽ Round toy you throw and catch', category: 'Toys', difficulty: 'easy' },
  { word: 'BIRD', hint: '🐦 Animal with wings that flies', category: 'Animals', difficulty: 'easy' },
  { word: 'BOOK', hint: '📚 Has pages and words to read', category: 'Objects', difficulty: 'easy' },
  { word: 'CAKE', hint: '🎂 Sweet dessert for birthdays', category: 'Food', difficulty: 'easy' },
  { word: 'DUCK', hint: '🦆 Yellow bird that swims and quacks', category: 'Animals', difficulty: 'easy' },
  { word: 'FISH', hint: '🐟 Animal that swims in water', category: 'Animals', difficulty: 'easy' },
  { word: 'GAME', hint: '🎮 Fun activity you play', category: 'Activities', difficulty: 'easy' },
  { word: 'HAND', hint: '✋ Part of your body with fingers', category: 'Body', difficulty: 'easy' },
  { word: 'JUMP', hint: '🦘 What you do with both feet off ground', category: 'Actions', difficulty: 'easy' },
  { word: 'KITE', hint: '🪁 Toy that flies in the wind', category: 'Toys', difficulty: 'easy' },
  { word: 'LAKE', hint: '🏞️ Large body of water', category: 'Nature', difficulty: 'easy' },
  { word: 'MILK', hint: '🥛 White drink that comes from cows', category: 'Food', difficulty: 'easy' },
  { word: 'MOON', hint: '🌙 White circle in the night sky', category: 'Space', difficulty: 'easy' },
  { word: 'NOSE', hint: '👃 Part of face used for smelling', category: 'Body', difficulty: 'easy' },
  { word: 'PARK', hint: '🏞️ Place with grass and playground', category: 'Places', difficulty: 'easy' },
  
  // 5-letter words
  { word: 'APPLE', hint: '🍎 Red or green fruit that grows on trees', category: 'Food', difficulty: 'easy' },
  { word: 'BREAD', hint: '🍞 Food made from flour that you toast', category: 'Food', difficulty: 'easy' },
  { word: 'CHAIR', hint: '🪑 Furniture you sit on', category: 'Objects', difficulty: 'easy' },
  { word: 'DANCE', hint: '💃 Moving your body to music', category: 'Actions', difficulty: 'easy' },
  { word: 'EARTH', hint: '🌍 The planet we live on', category: 'Science', difficulty: 'easy' },
  { word: 'FLAME', hint: '🔥 Hot orange light from fire', category: 'Elements', difficulty: 'easy' },
  { word: 'GRAPE', hint: '🍇 Small round fruit in bunches', category: 'Food', difficulty: 'easy' },
  { word: 'HAPPY', hint: '😊 Feeling good and smiling', category: 'Emotions', difficulty: 'easy' },
  { word: 'HOUSE', hint: '🏠 Place where people live', category: 'Home', difficulty: 'easy' },
  { word: 'LAUGH', hint: '😂 Sound you make when something is funny', category: 'Actions', difficulty: 'easy' },
  { word: 'MAGIC', hint: '✨ Special tricks that seem impossible', category: 'Fantasy', difficulty: 'easy' },
  { word: 'MOUSE', hint: '🐭 Small furry animal with a long tail', category: 'Animals', difficulty: 'easy' },
  { word: 'MUSIC', hint: '🎵 Sounds that make songs', category: 'Arts', difficulty: 'easy' },
  { word: 'OCEAN', hint: '🌊 Very large body of salt water', category: 'Nature', difficulty: 'easy' },
  { word: 'PLANT', hint: '🌱 Green living thing that grows in soil', category: 'Nature', difficulty: 'easy' },
  { word: 'RIVER', hint: '🏞️ Water that flows to the ocean', category: 'Nature', difficulty: 'easy' },
  { word: 'SMILE', hint: '😊 What you do when you are happy', category: 'Actions', difficulty: 'easy' },
  { word: 'SNAKE', hint: '🐍 Long animal with no legs', category: 'Animals', difficulty: 'easy' },
  { word: 'SPACE', hint: '🚀 Area above Earth with stars', category: 'Science', difficulty: 'easy' },
  { word: 'STORY', hint: '📖 Tale with characters and events', category: 'Literature', difficulty: 'easy' },
  { word: 'SWEET', hint: '🍭 Taste like candy or sugar', category: 'Taste', difficulty: 'easy' },
  { word: 'TABLE', hint: '🪑 Flat surface with legs for eating', category: 'Furniture', difficulty: 'easy' },
  { word: 'TIGER', hint: '🐅 Large orange cat with black stripes', category: 'Animals', difficulty: 'easy' },
  { word: 'TRUCK', hint: '🚛 Big vehicle that carries heavy things', category: 'Transportation', difficulty: 'easy' },
  { word: 'WATER', hint: '💧 Clear liquid that we drink', category: 'Nature', difficulty: 'easy' },
  
  // 6-letter words
  { word: 'ANIMAL', hint: '🐾 Living creature like dogs and cats', category: 'Nature', difficulty: 'easy' },
  { word: 'BASKET', hint: '🧺 Container for carrying things', category: 'Objects', difficulty: 'easy' },
  { word: 'CASTLE', hint: '🏰 Big stone building where kings live', category: 'Buildings', difficulty: 'easy' },
  { word: 'COOKIE', hint: '🍪 Sweet baked treat', category: 'Food', difficulty: 'easy' },
  { word: 'FLOWER', hint: '🌸 Pretty colored part of plants', category: 'Nature', difficulty: 'easy' },
  { word: 'FRIEND', hint: '👫 Someone you like to play with', category: 'People', difficulty: 'easy' },
  { word: 'JUNGLE', hint: '🌴 Forest with lots of wild animals', category: 'Places', difficulty: 'easy' },
  { word: 'KITTEN', hint: '🐱 Baby cat', category: 'Animals', difficulty: 'easy' },
  { word: 'LETTER', hint: '✉️ Message you send to someone', category: 'Communication', difficulty: 'easy' },
  { word: 'MARKET', hint: '🛒 Place where you buy food', category: 'Places', difficulty: 'easy' },
  { word: 'NATURE', hint: '🌿 Trees, animals, and outdoor world', category: 'Environment', difficulty: 'easy' },
  { word: 'ORANGE', hint: '🍊 Round orange fruit', category: 'Food', difficulty: 'easy' },
  { word: 'PICNIC', hint: '🧺 Eating outside on a blanket', category: 'Activities', difficulty: 'easy' },
  { word: 'RABBIT', hint: '🐰 Furry animal with long ears', category: 'Animals', difficulty: 'easy' },
  { word: 'SCHOOL', hint: '🏫 Place where children learn', category: 'Places', difficulty: 'easy' },
  { word: 'SIMPLE', hint: '✅ Easy to understand or do', category: 'Descriptions', difficulty: 'easy' },
  { word: 'TURTLE', hint: '🐢 Slow animal with a shell', category: 'Animals', difficulty: 'easy' },
  { word: 'WINDOW', hint: '🪟 Glass opening in walls', category: 'Home', difficulty: 'easy' },
  { word: 'WINTER', hint: '❄️ Cold season with snow', category: 'Seasons', difficulty: 'easy' },
  { word: 'YELLOW', hint: '💛 Color of the sun and bananas', category: 'Colors', difficulty: 'easy' },

  // Medium difficulty (6-7 letters) - 20% of total (20 words)
  { word: 'BALLOON', hint: '🎈 Inflated decoration that floats', category: 'Objects', difficulty: 'medium' },
  { word: 'BICYCLE', hint: '🚲 Two-wheeled vehicle you pedal', category: 'Transportation', difficulty: 'medium' },
  { word: 'CHICKEN', hint: '🐔 Farm bird that lays eggs', category: 'Animals', difficulty: 'medium' },
  { word: 'DOLPHIN', hint: '🐬 Smart sea animal that jumps', category: 'Animals', difficulty: 'medium' },
  { word: 'ELEPHANT', hint: '🐘 Large animal with a long trunk', category: 'Animals', difficulty: 'medium' },
  { word: 'GIRAFFE', hint: '🦒 Tall animal with a long neck', category: 'Animals', difficulty: 'medium' },
  { word: 'KITCHEN', hint: '🍳 Room where you cook food', category: 'Home', difficulty: 'medium' },
  { word: 'LIBRARY', hint: '📚 Place with lots of books', category: 'Places', difficulty: 'medium' },
  { word: 'MONSTER', hint: '👹 Scary creature from stories', category: 'Fantasy', difficulty: 'medium' },
  { word: 'PACKAGE', hint: '📦 Box or bag with something inside', category: 'Objects', difficulty: 'medium' },
  { word: 'PENGUIN', hint: '🐧 Black and white bird that cannot fly', category: 'Animals', difficulty: 'medium' },
  { word: 'RAINBOW', hint: '🌈 Colorful arc in the sky after rain', category: 'Nature', difficulty: 'medium' },
  { word: 'SANDWICH', hint: '🥪 Food between two pieces of bread', category: 'Food', difficulty: 'medium' },
  { word: 'TEACHER', hint: '👩‍🏫 Person who helps you learn at school', category: 'People', difficulty: 'medium' },
  { word: 'UNICORN', hint: '🦄 Magical horse with one horn', category: 'Fantasy', difficulty: 'medium' },
  { word: 'VILLAGE', hint: '🏘️ Small town with few houses', category: 'Places', difficulty: 'medium' },
  { word: 'WEATHER', hint: '🌤️ If it is sunny, rainy, or snowy', category: 'Nature', difficulty: 'medium' },
  { word: 'MORNING', hint: '🌅 Early part of the day', category: 'Time', difficulty: 'medium' },
  { word: 'PICTURE', hint: '🖼️ Image you can look at', category: 'Arts', difficulty: 'medium' },
  { word: 'GARDEN', hint: '🌻 Place where flowers and plants grow', category: 'Nature', difficulty: 'medium' },

  // Hard difficulty (8+ letters) - 10% of total (10 words)
  { word: 'ADVENTURE', hint: '🗺️ An exciting and dangerous experience', category: 'Actions', difficulty: 'hard' },
  { word: 'BUTTERFLY', hint: '🦋 Colorful insect with large wings', category: 'Animals', difficulty: 'hard' },
  { word: 'CHOCOLATE', hint: '🍫 Sweet brown treat that melts', category: 'Food', difficulty: 'hard' },
  { word: 'COMPUTER', hint: '💻 Electronic machine for games and work', category: 'Technology', difficulty: 'hard' },
  { word: 'DINOSAUR', hint: '🦕 Large extinct reptile from long ago', category: 'Animals', difficulty: 'hard' },
  { word: 'HOSPITAL', hint: '🏥 Place where doctors help sick people', category: 'Places', difficulty: 'hard' },
  { word: 'PLAYGROUND', hint: '🛝 Place with swings and slides for children', category: 'Places', difficulty: 'hard' },
  { word: 'TELEPHONE', hint: '📞 Device used to talk to people far away', category: 'Technology', difficulty: 'hard' },
  { word: 'UMBRELLA', hint: '☂️ Keeps you dry when it rains', category: 'Objects', difficulty: 'hard' },
  { word: 'BIRTHDAY', hint: '🎂 Special day when you celebrate getting older', category: 'Events', difficulty: 'hard' }
];

const generateAvailableLetters = (word: string): string[] => {
  const wordLetters = word.split('');
  const extraLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    .split('')
    .filter(letter => !wordLetters.includes(letter))
    .slice(0, Math.max(0, 12 - wordLetters.length));
  
  return [...wordLetters, ...extraLetters].sort(() => Math.random() - 0.5);
};

const generatePrepopulatedLetters = (word: Word): boolean[] => {
  const wordLength = word.word.length;
  const revealedLetters = new Array(wordLength).fill(false);
  
  let numToReveal = 0;
  switch (word.difficulty) {
    case 'easy':
      if (wordLength <= 3) {
        numToReveal = 1; // For 3-letter words, reveal just the first letter
      } else if (wordLength <= 4) {
        numToReveal = 1; // For 4-letter words, reveal 1 letter
      } else {
        numToReveal = Math.min(2, Math.floor(wordLength * 0.35)); // For 5-6 letter words, reveal up to 35%
      }
      break;
    case 'medium':
      numToReveal = Math.min(2, Math.floor(wordLength * 0.3)); // Reveal 30% or 2 letters max
      break;
    case 'hard':
      numToReveal = Math.min(3, Math.floor(wordLength * 0.25)); // Reveal 25% or 3 letters max
      break;
  }
  
  // Always reveal the first letter for easier start
  if (numToReveal > 0) {
    revealedLetters[0] = true;
    numToReveal--;
  }
  
  // For 3-letter words, don't reveal more letters to keep it challenging but fair
  if (wordLength <= 3) {
    return revealedLetters;
  }
  
  // Reveal additional random letters (not the last letter to keep challenge)
  const availablePositions = [];
  for (let i = 1; i < wordLength - 1; i++) {
    availablePositions.push(i);
  }
  
  for (let i = 0; i < numToReveal && availablePositions.length > 0; i++) {
    const randomIndex = Math.floor(Math.random() * availablePositions.length);
    const position = availablePositions.splice(randomIndex, 1)[0];
    revealedLetters[position] = true;
  }
  
  return revealedLetters;
};

const getNextUnrevealedIndex = (revealedLetters: boolean[]): number => {
  return revealedLetters.findIndex(revealed => !revealed);
};

const getRandomWord = (difficultyFilter: 'all' | 'easy' | 'medium' | 'hard' = 'all'): Word => {
  const filteredWords = difficultyFilter === 'all' 
    ? VOCABULARY_WORDS 
    : VOCABULARY_WORDS.filter(word => word.difficulty === difficultyFilter);
  
  if (filteredWords.length === 0) {
    // Fallback to all words if filter results in empty array
    return VOCABULARY_WORDS[Math.floor(Math.random() * VOCABULARY_WORDS.length)];
  }
  
  return filteredWords[Math.floor(Math.random() * filteredWords.length)];
};

const VocabularyVault: React.FC = () => {
  const initializeGame = (difficultyFilter: 'all' | 'easy' | 'medium' | 'hard' = 'all') => {
    const word = getRandomWord(difficultyFilter);
    const prepopulatedLetters = generatePrepopulatedLetters(word);
    return {
      gameState: {
        currentWord: word,
        revealedLetters: prepopulatedLetters,
        usedLetters: [], // This will now only track incorrect letters
        availableLetters: generateAvailableLetters(word.word),
        incorrectGuesses: 0,
        showHint: true, // Show hint by default
        gameStatus: 'playing' as const,
        difficultyFilter: difficultyFilter,
        isLoading: false
      },
      currentLetterIndex: getNextUnrevealedIndex(prepopulatedLetters)
    };
  };

  const [gameData, setGameData] = useState(() => initializeGame());
  const [gameState, setGameState] = useState<GameState>(gameData.gameState);
  const [score, setScore] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>('');
  const [currentLetterIndex, setCurrentLetterIndex] = useState<number>(gameData.currentLetterIndex);
  const { updateProgress } = useGameContext();

  const maxIncorrectGuesses = 3;

  const handleLetterGuess = (letter: string) => {
    if (gameState.gameStatus !== 'playing') {
      return;
    }

    // Check if this letter was already guessed incorrectly
    if (gameState.usedLetters.includes(letter)) {
      setFeedback('You already tried that letter!');
      setTimeout(() => setFeedback(''), 1500);
      return;
    }

    const wordArray = gameState.currentWord.word.split('');
    
    if (currentLetterIndex < wordArray.length && wordArray[currentLetterIndex] === letter) {
      // Correct letter in correct position
      const newRevealedLetters = [...gameState.revealedLetters];
      newRevealedLetters[currentLetterIndex] = true;
      
      setGameState(prev => ({
        ...prev,
        revealedLetters: newRevealedLetters
        // Don't add correct letters to usedLetters - they can be reused
      }));

      const nextIndex = getNextUnrevealedIndex(newRevealedLetters);
      setCurrentLetterIndex(nextIndex);
      setFeedback('✨ Correct! Keep going! ✨');
      playSound('success');

      // Check if word is complete (no more unrevealed letters)
      if (nextIndex === -1) {
        setGameState(prev => ({ ...prev, gameStatus: 'completed' }));
        const pointsEarned = gameState.currentWord.difficulty === 'easy' ? 1 : 
                           gameState.currentWord.difficulty === 'medium' ? 2 : 3;
        setScore(prevScore => prevScore + pointsEarned);
        setFeedback('🎉 Fantastic! You unlocked the vault! 🎉');
        updateProgress('vocabulary-vault', score + pointsEarned);
        
        // Auto-progress to next word after celebration
        setTimeout(() => {
          setGameState(prev => ({ ...prev, isLoading: true }));
          setFeedback('🚀 Loading next word...');
          
          setTimeout(() => {
            const newGame = initializeGame(gameState.difficultyFilter);
            setGameData(newGame);
            setGameState(newGame.gameState);
            setCurrentLetterIndex(newGame.currentLetterIndex);
            setFeedback('');
          }, 1500); // Show loading for 1.5 seconds
        }, 2000); // Wait 2 seconds to celebrate
      }
    } else {
      // Incorrect letter - add to usedLetters to prevent re-selection
      const newUsedLetters = [...gameState.usedLetters, letter];
      const newIncorrectGuesses = gameState.incorrectGuesses + 1;
      
      setGameState(prev => ({
        ...prev,
        incorrectGuesses: newIncorrectGuesses,
        usedLetters: newUsedLetters
      }));

      if (newIncorrectGuesses >= maxIncorrectGuesses) {
        setGameState(prev => ({ ...prev, gameStatus: 'failed' }));
        setFeedback(`The word was "${gameState.currentWord.word}". Try again!`);
        
        // Auto-progress to next word after showing the answer
        setTimeout(() => {
          setGameState(prev => ({ ...prev, isLoading: true }));
          setFeedback('🔄 Getting a new word for you...');
          
          setTimeout(() => {
            const newGame = initializeGame(gameState.difficultyFilter);
            setGameData(newGame);
            setGameState(newGame.gameState);
            setCurrentLetterIndex(newGame.currentLetterIndex);
            setFeedback('');
          }, 1500); // Show loading for 1.5 seconds
        }, 3000); // Wait 3 seconds to show the correct answer
      } else {
        setFeedback(`Not quite right. You have ${maxIncorrectGuesses - newIncorrectGuesses} attempts left.`);
      }
      playSound('error');
    }

    // Clear feedback after 2 seconds
    setTimeout(() => setFeedback(''), 2000);
  };

  const playSound = (type: 'success' | 'error' | 'click') => {
    console.log(`Playing ${type} sound`);
    // In a real implementation, audio files would be played here
  };

  const handleNewWord = () => {
    const newGame = initializeGame(gameState.difficultyFilter);
    setGameData(newGame);
    setGameState(newGame.gameState);
    setCurrentLetterIndex(newGame.currentLetterIndex);
    setFeedback('');
  };

  const handleDifficultyChange = (difficulty: 'all' | 'easy' | 'medium' | 'hard') => {
    const newGame = initializeGame(difficulty);
    setGameData(newGame);
    setGameState(newGame.gameState);
    setCurrentLetterIndex(newGame.currentLetterIndex);
    setFeedback('');
  };



  const getProgressPercentage = () => {
    const totalLetters = gameState.currentWord.word.length;
    const revealedCount = gameState.revealedLetters.filter(revealed => revealed).length;
    return (revealedCount / totalLetters) * 100;
  };

  return (
    <Container>
      <PageTitle>Vocabulary Vault</PageTitle>

      <GameContainer>
        <FlexContainer justify="space-between">
          <Link to="/">
            <Button variant="secondary">Back to Games</Button>
          </Link>

          <ScoreDisplay>
            Score: <span>{score}</span>
          </ScoreDisplay>
        </FlexContainer>

        <VaultContainer isCompleted={gameState.gameStatus === 'completed'}>
          {gameState.isLoading ? '🚀' : '🏆'}
        </VaultContainer>

        <DifficultyFilters>
          <FilterButton 
            isActive={gameState.difficultyFilter === 'all'}
            onClick={() => handleDifficultyChange('all')}
          >
            All Words (100)
          </FilterButton>
          <FilterButton 
            isActive={gameState.difficultyFilter === 'easy'}
            onClick={() => handleDifficultyChange('easy')}
          >
            Easy ⭐ (70)
          </FilterButton>
          <FilterButton 
            isActive={gameState.difficultyFilter === 'medium'}
            onClick={() => handleDifficultyChange('medium')}
          >
            Medium ⭐⭐ (20)
          </FilterButton>
          <FilterButton 
            isActive={gameState.difficultyFilter === 'hard'}
            onClick={() => handleDifficultyChange('hard')}
          >
            Hard ⭐⭐⭐ (10)
          </FilterButton>
        </DifficultyFilters>

        <WordDisplay>
          Category: {gameState.currentWord.category}
          <br />
          Difficulty: {gameState.currentWord.difficulty === 'easy' ? '⭐' : 
                       gameState.currentWord.difficulty === 'medium' ? '⭐⭐' : '⭐⭐⭐'}
        </WordDisplay>

        <ProgressBar>
          <ProgressFill percentage={getProgressPercentage()} />
        </ProgressBar>

        <LetterSlots>
          {gameState.currentWord.word.split('').map((letter, index) => (
            <LetterSlot
              key={index}
              isRevealed={gameState.revealedLetters[index]}
              isCorrect={index < currentLetterIndex ? true : index === currentLetterIndex ? undefined : undefined}
            >
              {gameState.revealedLetters[index] ? letter : ''}
            </LetterSlot>
          ))}
        </LetterSlots>

        <WordDisplay style={{ fontSize: '1.5rem', backgroundColor: 'rgba(255, 193, 7, 0.2)' }}>
          <SparkleContainer>
            💡 Hint: {gameState.currentWord.hint}
          </SparkleContainer>
        </WordDisplay>

        {gameState.isLoading ? (
          <LoadingContainer>
            <LoadingText>✨ Great job! Getting your next word ready... ✨</LoadingText>
            <div style={{ fontSize: '3rem', margin: '1rem 0' }}>🎯 🌟 🎮</div>
            <div style={{ fontSize: '1rem', color: '#666' }}>Keep up the awesome work!</div>
          </LoadingContainer>
        ) : (
          <>
            <LetterButtons>
              {gameState.availableLetters.map((letter, index) => (
                <LetterButton
                  key={index}
                  onClick={() => handleLetterGuess(letter)}
                  isUsed={gameState.usedLetters.includes(letter)}
                  disabled={gameState.usedLetters.includes(letter) || gameState.gameStatus !== 'playing'}
                >
                  {letter}
                </LetterButton>
              ))}
            </LetterButtons>

            {feedback && (
              <FeedbackMessage isCorrect={feedback.includes('✨') || feedback.includes('🎉')}>
                {feedback}
              </FeedbackMessage>
            )}

            {gameState.gameStatus === 'failed' && !gameState.isLoading && (
              <FlexContainer>
                <Button onClick={handleNewWord}>
                  Try Another Word Now
                </Button>
              </FlexContainer>
            )}

            <FlexContainer style={{ marginTop: '2rem', fontSize: '1rem', color: '#666' }}>
              <div>
                Incorrect guesses: {gameState.incorrectGuesses}/{maxIncorrectGuesses} | 
                Letters used: {gameState.usedLetters.length} | 
                Progress: {gameState.revealedLetters.filter(r => r).length}/{gameState.currentWord.word.length} letters | 
                Points: {gameState.currentWord.difficulty === 'easy' ? '1' : 
                         gameState.currentWord.difficulty === 'medium' ? '2' : '3'} pts
              </div>
            </FlexContainer>
          </>
        )}
      </GameContainer>
    </Container>
  );
};

export default VocabularyVault;